import { useController, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';

import {
  AppMessageSeverities,
  FunctionDisplayColors,
  type FunctionDisplay as FunctionDisplayType,
} from '@/types';
import { Button, MenuItem, TextField, Typography } from '@/ui';

import { useGetFunction } from '@/hooks/useGetFunction';
import { useGetFunctionDisplay } from '@/hooks/useGetFunctionDisplay';
import {
  useCreateFunctionDisplayMutation,
  useUpdateFunctionDisplayMutation,
} from '@/store/api/assistantApi';
import { useAppDispatch } from '@/store/hooks';
import { setAppMessage } from '@/store/slices/appSlice';
import { captureException } from '@/utils/errorMonitoring';

const FunctionDisplay = () => {
  const { functionId = '', assistantId = '', displayId = '' } = useParams();
  const {
    data = { color: FunctionDisplayColors.sage } as FunctionDisplayType,
  } = useGetFunctionDisplay(displayId);
  const { refetch } = useGetFunction(functionId);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const onBack = () =>
    navigate(`/coach/manage/assistants/${assistantId}/function/${functionId}`);

  const [createFunctionDisplay] = useCreateFunctionDisplayMutation();
  const [updateFunctionDisplay] = useUpdateFunctionDisplayMutation();

  const {
    control,
    formState: { isValid, isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: {
      color: data.color,
      icon: data.icon || '',
    },
    values: data,
  });

  const { field: color, fieldState: colorState } = useController({
    control,
    name: 'color',
    rules: { required: true },
  });
  const { field: icon, fieldState: iconState } = useController({
    control,
    name: 'icon',
    rules: { required: true },
  });

  const onSave = () => {
    handleSubmit(async (formData) => {
      try {
        if (displayId === '') {
          await createFunctionDisplay({ ...formData, functionId }).unwrap();
        } else {
          await updateFunctionDisplay({
            ...formData,
            id: displayId,
            functionId,
          }).unwrap();
        }
        refetch();
        dispatch(
          setAppMessage({
            message: 'Changes saved',
            open: true,
            severity: AppMessageSeverities.Success,
          }),
        );
      } catch (e) {
        captureException(e);
        dispatch(
          setAppMessage({
            message: 'Error saving assistant',
            open: true,
            severity: AppMessageSeverities.Error,
          }),
        );
      }
    })();
  };

  return (
    <div className="max-w-screen-sm p-6">
      <div className="flex flex-col gap-4">
        <Typography variant="h4">Function Display</Typography>

        <TextField
          error={colorState.invalid}
          helperText="Required"
          label="Color"
          variant="outlined"
          select
          {...color}
        >
          {Object.keys(FunctionDisplayColors).map((clr: string) => (
            <MenuItem key={clr} value={clr}>
              {clr}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          error={iconState.invalid}
          helperText="Required"
          label="Icon"
          variant="outlined"
          {...icon}
        />

        <div className="flex gap-2">
          <Button
            className="self-baseline"
            color="primary"
            variant="outlined"
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            className="self-baseline"
            color="primary"
            disabled={!isValid}
            loading={isSubmitting}
            variant="contained"
            onClick={onSave}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
export default FunctionDisplay;
